import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Zap } from 'lucide-react';

interface BankSafeIntroProps {
  onUnlock: () => void;
}

export const BankSafeIntro: React.FC<BankSafeIntroProps> = ({ onUnlock }) => {
  const [isOpening, setIsOpening] = useState(false);

  const handleOpenVault = () => {
    if (isOpening) return;
    setIsOpening(true);
    setTimeout(() => {
      onUnlock();
    }, 1350); // match animation timing
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      onClick={handleOpenVault}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#070301] overflow-hidden cursor-pointer select-none"
    >
      {/* Heavy Steel Vault Background & Environment with Opening Fade */}
      <motion.div 
        animate={isOpening ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#2b1207_0%,_#140702_50%,_#050201_100%)] pointer-events-none" 
      />
      
      {/* Ambient Lighting Vignette */}
      <motion.div 
        animate={isOpening ? { opacity: 0 } : { opacity: 0.85 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black pointer-events-none" 
      />

      {/* Heavy Steel Vault Chamber Viewport */}
      <div className="absolute inset-0 flex items-center justify-center perspective-[1600px] overflow-hidden">
        
        {/* LEFT VAULT DOOR WING (Spans full height, left half) */}
        <motion.div
          animate={isOpening ? { rotateY: -105, x: '-28%' } : { rotateY: 0, x: 0 }}
          transition={{ duration: 1.25, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformOrigin: 'left center' }}
          className="absolute left-0 top-0 bottom-0 w-1/2 bg-gradient-to-r from-[#140803] via-[#220e06] to-[#170a04] border-r border-[#caa062]/40 shadow-[inset_-25px_0_50px_rgba(0,0,0,0.9)] flex flex-col justify-between p-4 sm:p-8 z-20 pointer-events-none"
        >
          {/* Steel Reinforcement Texture */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,_rgba(216,170,104,0.06),_transparent_70%)]" />
        </motion.div>

        {/* RIGHT VAULT DOOR WING (Spans full height, right half) */}
        <motion.div
          animate={isOpening ? { rotateY: 105, x: '28%' } : { rotateY: 0, x: 0 }}
          transition={{ duration: 1.25, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformOrigin: 'right center' }}
          className="absolute right-0 top-0 bottom-0 w-1/2 bg-gradient-to-l from-[#140803] via-[#220e06] to-[#170a04] border-l border-[#caa062]/40 shadow-[inset_25px_0_50px_rgba(0,0,0,0.9)] flex flex-col justify-between p-4 sm:p-8 z-20 pointer-events-none items-end"
        >
          {/* Steel Reinforcement Texture */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,_rgba(216,170,104,0.06),_transparent_70%)]" />
        </motion.div>

        {/* =========================================================================
            VAULT STAGE: TRIPARTITE HERO LAYOUT (LEFT TEXT - CENTER CIRCLE - RIGHT CTA)
           ========================================================================= */}
        <motion.div
          animate={isOpening ? { scale: 1.08, opacity: 0, filter: "blur(4px)" } : { scale: 1, opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-30 w-full max-w-7xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-8 flex flex-col items-center justify-center min-h-screen sm:h-full sm:max-h-screen overflow-y-auto"
        >
          {/* Tripartite Grid */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8 md:gap-10 lg:gap-14 w-full my-auto">
            
            {/* =========================================================================
                LEFT WING: "IF YOU HAVE A GREAT BUSINESS STORY,"
               ========================================================================= */}
            <div 
              className="relative rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col items-center md:items-end text-center md:text-right w-full justify-center overflow-hidden border border-[#caa062]/35 shadow-[0_16px_40px_rgba(0,0,0,0.85),inset_0_1px_2px_rgba(255,229,163,0.15)]"
              style={{
                backgroundColor: '#1a0d07',
                backgroundImage: `
                  radial-gradient(ellipse at top right, rgba(95, 46, 22, 0.4) 0%, rgba(26, 13, 7, 0.95) 75%, #0e0603 100%),
                  url("data:image/svg+xml,%3Csvg width='180' height='180' viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='leather'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.18  0 0 0 0 0.09  0 0 0 0 0.04  0 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23leather)'/%3E%3C/svg%3E")
                `,
                backgroundBlendMode: 'normal, multiply'
              }}
            >
              {/* Saddle Stitch Perimeter Accent */}
              <div className="absolute inset-1.5 sm:inset-2 rounded-xl border border-dashed border-[#caa062]/30 pointer-events-none" />

              {/* Category Eyebrow */}
              <div className="relative z-10 inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:py-1 rounded bg-[#1e0d06]/90 border border-[#caa062]/40 text-[#ffd885] text-[9px] sm:text-xs font-mono uppercase tracking-widest mb-2 sm:mb-3.5 shadow-[0_4px_12px_rgba(0,0,0,0.7)]">
                <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#ffd885]" />
                <span>The Commercial Vision</span>
              </div>

              {/* Left Headline */}
              <h1 className="relative z-10 text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black text-white uppercase tracking-tight leading-snug drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)] space-y-1.5 sm:space-y-3">
                <span className="block text-gray-200">IF YOU HAVE A</span>
                <span className="block text-[#ffe5a3] drop-shadow-[0_0_24px_rgba(255,229,163,0.35)]">
                  GREAT BUSINESS STORY,
                </span>
              </h1>

              {/* Supporting Copy */}
              <p className="relative z-10 mt-3 sm:mt-5 text-[11px] sm:text-sm text-gray-300 font-sans max-w-xs sm:max-w-sm lg:max-w-md leading-relaxed hidden sm:block space-y-1 sm:space-y-2">
                <span className="block">Bring your ambitious market insight, domain expertise, and commercial goals.</span>
                <span className="block text-gray-400">Every breakthrough venture starts with an undeniable story.</span>
              </p>
            </div>

            {/* =========================================================================
                CENTER: COMBINATION WHEEL WITH FITTED LOGO & REFINED BROWN LEATHER BORDER
               ========================================================================= */}
            <div className="relative w-32 h-32 sm:w-44 sm:h-44 md:w-64 md:h-64 lg:w-76 lg:h-76 xl:w-84 xl:h-84 aspect-square shrink-0 mx-auto my-1.5 sm:my-3 md:my-0 select-none flex items-center justify-center">
              
              {/* Radial Glowing Amber Ambient Backlight */}
              <div className="absolute inset-[-15px] sm:inset-[-30px] rounded-full bg-[radial-gradient(circle_at_center,rgba(216,170,104,0.35)_0%,rgba(139,69,19,0.18)_50%,transparent_72%)] blur-2xl pointer-events-none" />

              {/* Precision Compass Radial Coordinates (N, S, E, W Rivets) */}
              <div className="absolute -top-2.5 sm:-top-4 left-1/2 -translate-x-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -bottom-2.5 sm:-bottom-4 left-1/2 -translate-x-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -left-2.5 sm:-left-4 top-1/2 -translate-y-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -right-2.5 sm:-right-4 top-1/2 -translate-y-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />

              {/* The Center Circle Bezel - True 1:1 circular aspect */}
              <div 
                className="relative w-full h-full aspect-square rounded-full p-1.5 sm:p-2.5 md:p-3 border-2 sm:border-3 md:border-4 border-[#caa062] bg-gradient-to-b from-[#3a1b0c] via-[#241006] to-[#120703] shadow-[0_24px_70px_rgba(0,0,0,0.98),0_0_40px_rgba(216,170,104,0.35),inset_0_2px_6px_rgba(255,255,255,0.25)] flex items-center justify-center transition-all duration-300"
              >
                {/* Fine Stitched Leather Inner Perimeter Ring */}
                <div className="absolute inset-1 sm:inset-1.5 md:inset-2 rounded-full aspect-square border border-dashed border-[#d8aa68]/50 pointer-events-none" />

                {/* Pelican Brand Logo */}
                <img 
                  src="/brand_logo.png?v=4" 
                  alt="The Brown Pelican Man Logo" 
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-contain rounded-full drop-shadow-[0_10px_28px_rgba(0,0,0,0.95)] select-none hover:scale-105 transition-transform duration-500" 
                />
              </div>
            </div>

            {/* =========================================================================
                RIGHT WING: "I'VE GOT A VALUABLE SOLUTION FOR YOU." & UNLOCK CTA
               ========================================================================= */}
            <div 
              className="relative rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col items-center md:items-start text-center md:text-left w-full justify-center overflow-hidden border border-[#caa062]/35 shadow-[0_16px_40px_rgba(0,0,0,0.85),inset_0_1px_2px_rgba(255,229,163,0.15)]"
              style={{
                backgroundColor: '#1a0d07',
                backgroundImage: `
                  radial-gradient(ellipse at top left, rgba(95, 46, 22, 0.4) 0%, rgba(26, 13, 7, 0.95) 75%, #0e0603 100%),
                  url("data:image/svg+xml,%3Csvg width='180' height='180' viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='leather'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.18  0 0 0 0 0.09  0 0 0 0 0.04  0 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23leather)'/%3E%3C/svg%3E")
                `,
                backgroundBlendMode: 'normal, multiply'
              }}
            >
              {/* Saddle Stitch Perimeter Accent */}
              <div className="absolute inset-1.5 sm:inset-2 rounded-xl border border-dashed border-[#caa062]/30 pointer-events-none" />

              {/* Category Eyebrow */}
              <div className="relative z-10 inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:py-1 rounded bg-[#1e0d06]/90 border border-[#caa062]/40 text-[#ffd885] text-[9px] sm:text-xs font-mono uppercase tracking-widest mb-2 sm:mb-3.5 shadow-[0_4px_12px_rgba(0,0,0,0.7)]">
                <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#ffd885]" />
                <span>The Scalable Architecture</span>
              </div>

              {/* Right Headline */}
              <h1 className="relative z-10 text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black text-white uppercase tracking-tight leading-snug drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)] space-y-1.5 sm:space-y-3">
                <span className="block text-gray-200">I’VE GOT A</span>
                <span className="block text-[#ffe5a3] drop-shadow-[0_0_24px_rgba(255,229,163,0.35)]">
                  VALUABLE SOLUTION FOR YOU.
                </span>
              </h1>

              {/* Supporting Copy */}
              <p className="relative z-10 mt-3 sm:mt-5 text-[11px] sm:text-sm text-gray-300 font-sans max-w-xs sm:max-w-sm lg:max-w-md leading-relaxed hidden sm:block space-y-1 sm:space-y-2">
                <span className="block">End-to-end systems architecture, high-converting product UX,</span>
                <span className="block text-gray-400">and resilient multi-cloud infrastructure engineered to scale your vision.</span>
              </p>
            </div>

          </div>

          {/* =========================================================================
              8 DOMAIN DISCIPLINES: EMPTY TOP & BOTTOM CORNERS OF BOTH SIDES
              Placed cleanly in the open top and bottom zones, completely clear of text, logo and buttons.
              Large, elegant non-highlighted "dream text" atmosphere.
             ========================================================================= */}

          {/* TOP-LEFT EMPTY ZONE: DEVELOPER & ARCHITECT */}
          <div className="pointer-events-none select-none absolute top-6 sm:top-8 md:top-10 left-6 sm:left-8 md:left-12 lg:left-16 z-0 hidden sm:flex flex-col items-start gap-1 sm:gap-1.5 opacity-[0.16] hover:opacity-30 transition-opacity duration-500">
            <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68]">
              DEVELOPER
            </span>
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68] pl-3 sm:pl-5 md:pl-7">
              ARCHITECT
            </span>
          </div>

          {/* BOTTOM-LEFT EMPTY ZONE: ANALYST & RESEARCHER */}
          <div className="pointer-events-none select-none absolute bottom-6 sm:bottom-8 md:bottom-10 left-6 sm:left-8 md:left-12 lg:left-16 z-0 hidden sm:flex flex-col items-start gap-1 sm:gap-1.5 opacity-[0.16] hover:opacity-30 transition-opacity duration-500">
            <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68]">
              ANALYST
            </span>
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68] pl-3 sm:pl-5 md:pl-7">
              RESEARCHER
            </span>
          </div>

          {/* TOP-RIGHT EMPTY ZONE: STRATEGIST & CONSULTANT (placed below Skip Intro button) */}
          <div className="pointer-events-none select-none absolute top-14 sm:top-16 md:top-12 right-6 sm:right-8 md:right-12 lg:right-16 z-0 hidden sm:flex flex-col items-end gap-1 sm:gap-1.5 opacity-[0.16] hover:opacity-30 transition-opacity duration-500">
            <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68]">
              STRATEGIST
            </span>
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68] pr-3 sm:pr-5 md:pr-7">
              CONSULTANT
            </span>
          </div>

          {/* BOTTOM-RIGHT EMPTY ZONE: INNOVATOR & MARKETER */}
          <div className="pointer-events-none select-none absolute bottom-6 sm:bottom-8 md:bottom-10 right-6 sm:right-8 md:right-12 lg:right-16 z-0 hidden sm:flex flex-col items-end gap-1 sm:gap-1.5 opacity-[0.16] hover:opacity-30 transition-opacity duration-500">
            <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68]">
              INNOVATOR
            </span>
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-display font-black uppercase tracking-[0.22em] text-[#d8aa68] pr-3 sm:pr-5 md:pr-7">
              MARKETER
            </span>
          </div>

          {/* Mobile Fallback: neatly distributed at very top and bottom */}
          <div className="pointer-events-none select-none absolute inset-x-0 bottom-2 z-0 flex sm:hidden flex-col items-center gap-1 opacity-[0.18]">
            <div className="flex items-center justify-center gap-2.5 text-[9px] font-display font-black uppercase tracking-[0.2em] text-[#d8aa68]">
              <span>DEVELOPER</span>
              <span className="text-[#caa062]/30">·</span>
              <span>ARCHITECT</span>
              <span className="text-[#caa062]/30">·</span>
              <span>ANALYST</span>
              <span className="text-[#caa062]/30">·</span>
              <span>RESEARCHER</span>
            </div>
            <div className="flex items-center justify-center gap-2.5 text-[9px] font-display font-black uppercase tracking-[0.2em] text-[#d8aa68]">
              <span>STRATEGIST</span>
              <span className="text-[#caa062]/30">·</span>
              <span>CONSULTANT</span>
              <span className="text-[#caa062]/30">·</span>
              <span>INNOVATOR</span>
              <span className="text-[#caa062]/30">·</span>
              <span>MARKETER</span>
            </div>
          </div>
        </motion.div>

      </div>
    </motion.div>
  );
};
